import { DestroyRef, inject, Provider } from '@angular/core';
import { AUDIO_PLAYER_SOURCE_RESOLVER, AudioSourceResolver } from '@lcs/shared/audio-player';

/**
 * The recording endpoint doesn't support HTTP range requests, so seeking re-downloads the whole
 * file from the start. This resolver fetches it once as a Blob instead, so seeking is instant and
 * local. It also cancels that fetch if a new url comes in first or the providing component is
 * destroyed, since the library's own resolver only ignores stale results, not the request itself.
 */
function createFetchBlobAudioSourceResolver(destroyRef: DestroyRef): AudioSourceResolver {
   let currentController: AbortController | null = null;

   destroyRef.onDestroy(() => currentController?.abort());

   return async (url: string) => {
      currentController?.abort();
      const controller = new AbortController();
      currentController = controller;

      const response = await fetch(url, { credentials: 'include', signal: controller.signal });
      if (!response.ok) {
         // Throwing here surfaces through the player's own error UI (errorMessage/errorTemplate)
         // instead of silently resolving to a broken source.
         throw new Error(`Failed to load audio recording (${response.status})`);
      }
      return response.blob();
   };
}

/** Provides the resolver above, scoped to the providing component's lifetime via its `providers`. */
export function provideAudioBlobSourceResolver(): Provider {
   return {
      provide: AUDIO_PLAYER_SOURCE_RESOLVER,
      useFactory: () => createFetchBlobAudioSourceResolver(inject(DestroyRef)),
   };
}
